import { useCallback, useEffect, useState } from 'react';

export interface SellerPromotion {
  id: string;
  marketplace: 'shopee' | 'mercadolivre';
  connectionId: string;
  connectionName: string;
  type: string;
  name: string;
  status: string;
  startTime?: number | null;
  endTime?: number | null;
  canManageInApp?: boolean;
  listingIdExt?: string;
  listingTitle?: string | null;
}

export interface PromotionAutomationSettings {
  autoEnabled: boolean;
  autoJoinEnabled: boolean;
  syncEnabled: boolean;
  marketplace: 'shopee' | 'mercadolivre' | null;
  connectionId: string | null;
  discountValue?: number | string;
  defaultCampaignStock?: number | string;
  adjustMlLightningStock?: boolean;
  adjustShopeeFlashStock?: boolean;
  createShopeeFlashSale?: boolean;
  createShopeeDiscount?: boolean;
  createShopeeBundleDeal?: boolean;
  createMlSellerCampaign?: boolean;
  createMlPriceDiscount?: boolean;
  createMlVolumeCampaign?: boolean;
  mlVolumeSubType?: string;
  mlVolumeBuyQty?: number | string;
  mlVolumePayQty?: number | string;
  mlVolumeDiscountPct?: number | string;
  bundleTiers?: Array<{ minQty: number; discountValue: number }>;
  lastRunAt?: string | null;
  lastCampaignSyncAt?: string | null;
  lastPromotionReconcileAt?: string | null;
  updatedAt?: string | null;
  lastRunResult?: any;
  lastLinkResult?: any;
  lastEnsureCampaignsResult?: any;
}

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function readJson(res: Response) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: res.ok ? 'Resposta invalida da API' : 'API indisponivel no momento' };
  }
}

export function useSellerPromotions(filters: { marketplace?: string; connectionId?: string; status?: string } = {}) {
  const [promotions, setPromotions] = useState<SellerPromotion[]>([]);
  const [settings, setSettings] = useState<PromotionAutomationSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPromotions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.marketplace) params.set('marketplace', filters.marketplace);
      if (filters.connectionId) params.set('connectionId', filters.connectionId);
      if (filters.status) params.set('status', filters.status);
      const res = await fetch(`/api/seller/promotions?${params.toString()}`, { headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao buscar promocoes');
      setPromotions(json.promotions || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar promocoes');
    } finally {
      setLoading(false);
    }
  }, [filters.marketplace, filters.connectionId, filters.status]);

  const fetchSettings = useCallback(async () => {
    const res = await fetch('/api/seller/promotions/automation', { headers: headers() });
    const json = await readJson(res);
    if (json.success) setSettings(json.settings);
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchSettings().catch(() => undefined);
  }, [fetchPromotions, fetchSettings]);

  const updateSettings = useCallback(async (patch: Partial<PromotionAutomationSettings>) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/automation', { method: 'PATCH', headers: headers(), body: JSON.stringify(patch) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao salvar automacao');
      setSettings(json.settings);
      return json.settings;
    } finally {
      setSaving(false);
    }
  }, []);

  const syncPromotions = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/sync', { method: 'POST', headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao sincronizar campanhas');
      await Promise.all([fetchSettings(), fetchPromotions()]);
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings]);

  // Campanhas disponiveis + em quais promocoes cada anuncio realmente esta.
  const reconcilePromotions = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/reconcile', { method: 'POST', headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao sincronizar promocoes');
      await Promise.all([fetchSettings(), fetchPromotions()]);
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings]);

  const createPromotion = useCallback(async (payload: any) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions', { method: 'POST', headers: headers(), body: JSON.stringify(payload) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao criar promocao');
      await fetchPromotions();
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions]);

  const runAutomation = useCallback(async (patch: Partial<PromotionAutomationSettings> = {}) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/automation/run', { method: 'POST', headers: headers(), body: JSON.stringify(patch) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao executar automacao');
      await fetchSettings();
      await fetchPromotions();
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings]);

  const checkCampaigns = useCallback(async (patch: Partial<PromotionAutomationSettings> = {}) => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/automation/check-campaigns', { method: 'POST', headers: headers(), body: JSON.stringify(patch) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao verificar campanhas');
      await fetchSettings();
      await fetchPromotions();
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings]);

  const ensureCampaigns = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/automation/ensure-campaigns', { method: 'POST', headers: headers(), body: JSON.stringify(filters) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao preparar campanhas');
      await fetchSettings();
      await fetchPromotions();
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings, filters.marketplace, filters.connectionId]);

  const linkListings = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/seller/promotions/automation/run', { method: 'POST', headers: headers(), body: JSON.stringify(filters) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao aderir anuncios');
      await fetchSettings();
      await fetchPromotions();
      return json;
    } finally {
      setSaving(false);
    }
  }, [fetchPromotions, fetchSettings, filters.marketplace, filters.connectionId]);
  return { promotions, settings, loading, saving, error, refetch: fetchPromotions, updateSettings, syncPromotions, reconcilePromotions, createPromotion, runAutomation, checkCampaigns, ensureCampaigns, linkListings };
}
